import { useTranslation } from "../i18n/LanguageContext";
import AvatarSVG, {
  AVATAR_BASES,
  AVATAR_SKIN_TONES,
  AVATAR_HAIR_STYLES,
  AVATAR_HAIR_COLORS,
  AVATAR_FACIAL_HAIR,
  AVATAR_EYEBROWS,
  AVATAR_EYE_STYLES,
  AVATAR_EYE_COLORS,
  AVATAR_MOUTHS,
  AVATAR_GLASSES,
} from "./AvatarSVG";

// Swatch/option picker row shared by every avatar attribute below.
function OptionRow({ label, options, value, onChange, renderOption }) {
  return (
    <div className="mb-3">
      <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-400">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            aria-pressed={value === opt}
            className={`rounded-lg border px-1 py-1 transition ${
              value === opt ? "border-sky-400 ring-2 ring-sky-400/60" : "border-slate-600 hover:border-slate-400"
            }`}
          >
            {renderOption ? renderOption(opt) : <span className="px-2 py-1 text-xs capitalize text-slate-100">{opt}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

const swatch = (color) => <span className="block h-6 w-6 rounded-full border border-slate-900/40" style={{ backgroundColor: color }} />;

// MVP PartyBuddy avatar builder — a modest, original (non-Bitmoji) set of
// swatch/shape pickers over the composable AvatarSVG layers. Intentionally
// small (a handful of options per attribute) so it stays simple and fast to
// use; more variety can be added later without changing the config shape
// stored on the user's profile (`avatarConfig`).
function AvatarEditor({ value, onChange }) {
  const { t } = useTranslation();
  const cfg = value;
  const set = (key) => (val) => onChange({ ...cfg, [key]: val });

  return (
    <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
      <div className="mb-4 flex justify-center">
        <AvatarSVG config={cfg} size={112} />
      </div>

      <OptionRow
        label={t("avatar.base", "Presentation")}
        options={AVATAR_BASES}
        value={cfg.base}
        onChange={set("base")}
        renderOption={(opt) => (
          <span className="px-2 py-1 text-xs capitalize text-slate-100">
            {opt === "female" ? t("avatar.baseFemale", "Female") : t("avatar.baseMale", "Male")}
          </span>
        )}
      />
      <OptionRow label={t("avatar.skinTone", "Skin tone")} options={AVATAR_SKIN_TONES} value={cfg.skinTone} onChange={set("skinTone")} renderOption={swatch} />
      <OptionRow
        label={t("avatar.hairStyle", "Hairstyle")}
        options={AVATAR_HAIR_STYLES}
        value={cfg.hairStyle}
        onChange={set("hairStyle")}
      />
      <OptionRow label={t("avatar.hairColor", "Hair color")} options={AVATAR_HAIR_COLORS} value={cfg.hairColor} onChange={set("hairColor")} renderOption={swatch} />
      <OptionRow
        label={t("avatar.facialHair", "Facial hair")}
        options={AVATAR_FACIAL_HAIR}
        value={cfg.facialHair}
        onChange={set("facialHair")}
      />
      <OptionRow label={t("avatar.eyebrows", "Eyebrows")} options={AVATAR_EYEBROWS} value={cfg.eyebrows} onChange={set("eyebrows")} />
      <OptionRow label={t("avatar.eyeStyle", "Eye style")} options={AVATAR_EYE_STYLES} value={cfg.eyeStyle} onChange={set("eyeStyle")} />
      <OptionRow label={t("avatar.eyeColor", "Eye color")} options={AVATAR_EYE_COLORS} value={cfg.eyeColor} onChange={set("eyeColor")} renderOption={swatch} />
      <OptionRow label={t("avatar.mouth", "Mouth")} options={AVATAR_MOUTHS} value={cfg.mouth} onChange={set("mouth")} />
      <OptionRow label={t("avatar.glasses", "Glasses")} options={AVATAR_GLASSES} value={cfg.glasses} onChange={set("glasses")} />
    </div>
  );
}

export default AvatarEditor;
